// src/components/AddDocument.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Divider,
    IconButton,
    Stepper,
    Step,
    StepLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    Description as DescriptionIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DocumentPreviewHandler from './DocumentPreviewHandler';

const API_URL = process.env.REACT_APP_API_URL || '';

const AddDocument = () => {
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadedId, setUploadedId] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [processingStatus, setProcessingStatus] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const navigate = useNavigate();

    // Steps for the upload process
    const steps = [
        'Wybór dokumentu',
        'Przesyłanie dokumentu',
        'Przetwarzanie OCR',
        'Zakończenie'
    ];

    useEffect(() => {
        if (file) {
            console.log('File selected:', file);
            setShowPreview(true);
        } else {
            setShowPreview(false);
        }
    }, [file]);

    // Effect for checking document status when in processing step
    useEffect(() => {
        if (uploadedId && activeStep === 2) {
            checkStatus();
        }
    }, [uploadedId, activeStep]);

    // Function to check document processing status
    const checkStatus = async () => {
        try {
            const response = await fetch(`${API_URL}/api/expenses/${uploadedId}/status`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to check document status');
            }

            const data = await response.json();
            setProcessingStatus(data.status);

            if (data.status === 'completed') {
                setActiveStep(3); // Move to final step
            } else {
                // Continue checking status every few seconds
                setTimeout(checkStatus, 3000);
            }
        } catch (error) {
            console.error('Error checking document status:', error);
            setError(`Błąd podczas sprawdzania statusu: ${error.message}`);
        }
    };

    // Handler for file selection
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            console.log('Selected file:', selectedFile);

            // Validate file type
            const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/tiff'];
            if (!validTypes.includes(selectedFile.type)) {
                setError('Nieprawidłowy typ pliku. Proszę wgrać plik PDF lub obraz (JPEG, PNG, TIFF).');
                return;
            }

            // Validate file size (10MB limit)
            if (selectedFile.size > 10 * 1024 * 1024) {
                setError('Plik jest za duży. Maksymalny rozmiar to 10MB.');
                return;
            }

            setFile(selectedFile);
            setError(null);
            setActiveStep(1); // Move to upload step
        }
    };

    // Handle document upload to backend
    const handleUpload = async () => {
        if (!file) return;

        setLoading(true);
        setError(null);

        // Create form data with the file
        const formData = new FormData();
        formData.append('document', file);

        try {
            const response = await fetch(`${API_URL}/api/expenses`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error('Błąd podczas przesyłania dokumentu');
            }

            const data = await response.json();
            console.log('Server response:', data);

            // Set uploaded ID and move to processing step
            setUploadedId(data.id);
            setActiveStep(2);
        } catch (err) {
            console.error('Error during upload:', err);
            setError(err.message);
            setActiveStep(1); // Stay on upload step for retry
        } finally {
            setLoading(false);
        }
    };

    // Handle dialog close and navigation
    const handleDialogClose = (viewDocument) => {
        setDialogOpen(false);
        if (viewDocument && uploadedId) {
            navigate(`/expenses/${uploadedId}`);
        } else {
            // Reset form
            setFile(null);
            setActiveStep(0);
            setUploadedId(null);
        }
    };

    // Handle completion and show success dialog
    const handleComplete = () => {
        setDialogOpen(true);
    };

    // Render main content based on active step
    const renderStepContent = () => {
        switch (activeStep) {
            case 0: // Document selection
                return (
                    <>
                        <Box sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            mb: 3
                        }}>
                            <input
                                type="file"
                                onChange={handleFileChange}
                                accept=".pdf,.jpg,.jpeg,.png,.tiff"
                                style={{ display: 'none' }}
                                id="file-upload"
                            />
                            <label htmlFor="file-upload">
                                <Button
                                    component="span"
                                    variant="outlined"
                                    startIcon={<UploadIcon />}
                                    size="large"
                                >
                                    Wybierz plik
                                </Button>
                            </label>

                            {file && (
                                <Typography sx={{ mt: 2, color: 'text.secondary' }}>
                                    Wybrany plik: {file.name}
                                </Typography>
                            )}

                            <Typography sx={{ mt: 3, color: 'text.secondary' }}>
                                Obsługiwane formaty: PDF, JPEG, PNG, TIFF (max. 10MB)
                            </Typography>
                        </Box>
                    </>
                );

            case 1: // Upload step
                return (
                    <>
                        {showPreview && file && (
                            <Box sx={{ mb: 3 }}>
                                <DocumentPreviewHandler file={file} />
                            </Box>
                        )}

                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleUpload}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
                            size="large"
                        >
                            {loading ? 'Przesyłanie...' : 'Prześlij dokument'}
                        </Button>
                    </>
                );

            case 2: // Processing OCR
                return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={60} />
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Trwa przetwarzanie dokumentu przez OCR...
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            To może potrwać kilka minut. Nie zamykaj tej strony.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 3 }}>
                            Status: {
                                processingStatus === 'in_progress' ? 'W trakcie analizy' :
                                    processingStatus === 'processing' ? 'Przetwarzanie' :
                                        processingStatus || 'Oczekiwanie...'
                            }
                        </Typography>
                    </Box>
                );

            case 3: // Completed
                return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 60 }} />
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            Dokument został pomyślnie przetworzony!
                        </Typography>
                        <Box sx={{ mt: 4 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleComplete}
                                size="large"
                            >
                                Zakończ
                            </Button>
                        </Box>
                    </Box>
                );

            default:
                return null;
        }
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                <Paper sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton onClick={() => navigate('/expenses')} edge="start">
                                <ArrowBackIcon />
                            </IconButton>
                            <Typography variant="h5" component="h1">
                                Wgraj dokument do OCR
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                            <IconButton
                                size="small"
                                aria-label="close"
                                color="inherit"
                                onClick={() => setError(null)}
                                sx={{ ml: 1 }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Alert>
                    )}

                    {/* Stepper */}
                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {/* Main content based on step */}
                    <Box sx={{ py: 2 }}>
                        {renderStepContent()}
                    </Box>
                </Paper>
            </Box>

            {/* Success dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => handleDialogClose(false)}
                aria-labelledby="success-dialog-title"
            >
                <DialogTitle id="success-dialog-title">
                    Dokument przetworzony pomyślnie!
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Dokument został pomyślnie przesłany i przetworzony. Co chcesz zrobić dalej?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleDialogClose(false)}>
                        Dodaj kolejny dokument
                    </Button>
                    <Button onClick={() => handleDialogClose(true)} autoFocus variant="contained" color="primary">
                        Przejdź do szczegółów dokumentu
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AddDocument;